
import { Options, Vue } from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import * as OM from '@/Model';

@Options({})
export default class EditWristBandAccessoryModal extends Vue {

    @Prop() estimationIdentifier: string;
    @Prop() productionProcessIdentifier: string;
    @Prop() wristBand: OM.WristBandCalculation;
    @Prop() availableMaterials: OM.RawMaterialConfigurationVM[];
    @Prop() saveCallback: (model: OM.EditWristBandAccessoryVM) => Promise<any>;

    bands: OM.EditKnitBandVariantVM[] = [];
    activeBandIndex: number = 0;
    tempMaterialIdentifier: string = "";
    tempPercentage: number = null;
    serverErrors: OM.MaterialCompositionError[] = [];

    created() {
        let source = this.wristBand && this.wristBand.variants && this.wristBand.variants.length > 0
            ? this.wristBand.variants
            : [this.wristBand];

        this.bands = source.filter(x => !!x).map(x => this.toEditableBand(x));
        if (this.bands.length == 0)
            this.bands.push(this.createEmptyBand());
    }

    get activeBand(): OM.EditKnitBandVariantVM {
        return this.bands[this.activeBandIndex];
    }

    get materials(): OM.MaterialShareVM[] {
        return this.activeBand ? this.activeBand.materials : [];
    }

    get weightFromDimensions(): number {
        if (!this.activeBand)
            return 0;

        return (Number(this.activeBand.bandFlatWidthCm) || 0) *
            (Number(this.activeBand.bandFlatLengthCm) || 0) *
            2 / 10000 *
            (Number(this.activeBand.fabricGsm) || 0);
    }

    get formattedWeight(): string {
        return this.weightFromDimensions.toLocaleString('it-IT', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
    }

    get formattedTotalWeight(): string {
        return (this.weightFromDimensions * (Number(this.activeBand.quantity) || 0))
            .toLocaleString('it-IT', { minimumFractionDigits: 3, maximumFractionDigits: 3 });
    }

    get selectableMaterials(): OM.RawMaterialConfigurationVM[] {
        return (this.availableMaterials || []).filter(x =>
            !this.materials.some(material => material.materialIdentifier == x.identifier))
            .sort((a, b) => a.materialName.trim().localeCompare(b.materialName.trim(), undefined, { sensitivity: 'base' }));
    }

    get totalPercentage(): number {
        return this.materials.reduce((sum, item) => sum + (Number(item.percentage) || 0), 0);
    }

    get remainingPercentage(): number {
        return Math.max(0, 100 - this.totalPercentage);
    }

    get compositionIsValid(): boolean {
        return this.materials.length > 0 && Math.abs(this.totalPercentage - 100) <= 0.010000001;
    }

    get addDisabled(): boolean {
        return !this.tempMaterialIdentifier ||
            !this.tempPercentage ||
            this.tempPercentage <= 0 ||
            this.remainingPercentage <= 0;
    }

    get totalQuantity(): number {
        return this.bands.reduce((sum, band) => sum + (this.quantityIsValid(band.quantity) ? Number(band.quantity) : 0), 0);
    }

    get isValid(): boolean {
        return this.bands.length > 0 && this.bands.every(band => this.bandIsValid(band));
    }

    get globalErrors(): string[] {
        return this.serverErrors.map(x => x.message);
    }

    quantityIsValid(value: number): boolean {
        let quantity = Number(value);
        return Number.isInteger(quantity) && quantity > 0;
    }

    bandIsValid(band: OM.EditKnitBandVariantVM): boolean {
        let totalPercentage = (band.materials || [])
            .reduce((sum, item) => sum + (Number(item.percentage) || 0), 0);

        return this.quantityIsValid(band.quantity) &&
            Number(band.bandFlatWidthCm) > 0 &&
            Number(band.bandFlatLengthCm) > 0 &&
            Number(band.fabricGsm) > 0 &&
            band.materials.length > 0 &&
            Math.abs(totalPercentage - 100) <= 0.010000001 &&
            band.materials.every(x => !!x.materialIdentifier);
    }

    selectBand(index: number) {
        this.activeBandIndex = index;
        this.resetTemporaryInputs();
    }

    addBand() {
        let newBand = this.activeBand ? this.cloneEditableBand(this.activeBand) : this.createEmptyBand();
        newBand.quantity = 1;
        this.bands.push(newBand);
        this.activeBandIndex = this.bands.length - 1;
        this.resetTemporaryInputs();
    }

    removeBand() {
        if (this.bands.length <= 1)
            return;

        this.bands.splice(this.activeBandIndex, 1);
        this.activeBandIndex = Math.min(this.activeBandIndex, this.bands.length - 1);
        this.resetTemporaryInputs();
    }

    addMaterial() {
        if (this.addDisabled)
            return;

        let selected = (this.availableMaterials || []).find(x => x.identifier == this.tempMaterialIdentifier);
        if (!selected)
            return;

        let material = new OM.MaterialShareVM();
        material.materialIdentifier = selected.identifier;
        material.materialName = selected.materialName;
        material.percentage = Math.min(this.tempPercentage, this.remainingPercentage);
        this.materials.push(material);

        this.resetTemporaryInputs();
    }

    removeMaterial(index: number) {
        this.materials.splice(index, 1);
        this.serverErrors = [];
    }

    save() {
        let model = new OM.EditWristBandAccessoryVM();
        model.estimationIdentifier = this.estimationIdentifier;
        model.productionProcessIdentifier = this.productionProcessIdentifier;
        model.bands = this.bands.map(band => {
            let result = this.cloneEditableBand(band);
            result.quantity = Number(band.quantity);
            result.bandFlatWidthCm = Number(band.bandFlatWidthCm);
            result.bandFlatLengthCm = Number(band.bandFlatLengthCm);
            result.fabricGsm = Number(band.fabricGsm);
            result.fabricWeightUsedGrams = result.bandFlatWidthCm * result.bandFlatLengthCm * 2 / 10000 * result.fabricGsm;
            result.materials.forEach(material => material.percentage = Number(material.percentage));
            return result;
        });

        this.serverErrors = [];
        this.saveCallback(model)
            .catch(err => {
                this.serverErrors = err && err.errors ? err.errors : [];
            });
    }

    private toEditableBand(source: OM.WristBandCalculation): OM.EditKnitBandVariantVM {
        let band = new OM.EditKnitBandVariantVM();
        band.quantity = source.quantity > 0 ? source.quantity : 1;
        band.bandFlatWidthCm = source.bandFlatWidthCm;
        band.bandFlatLengthCm = source.bandFlatLengthCm;
        band.fabricGsm = source.fabricGsm;
        band.fabricWeightUsedGrams = source.fabricWeightUsedGrams;
        band.materials = (source.materials || []).map(share => {
            let material = new OM.MaterialShareVM();
            material.materialIdentifier = share.material.identifier;
            material.materialName = share.material.text;
            material.percentage = share.percentage;
            return material;
        });
        return band;
    }

    private cloneEditableBand(source: OM.EditKnitBandVariantVM): OM.EditKnitBandVariantVM {
        let band = new OM.EditKnitBandVariantVM();
        band.quantity = source.quantity;
        band.bandFlatWidthCm = source.bandFlatWidthCm;
        band.bandFlatLengthCm = source.bandFlatLengthCm;
        band.fabricGsm = source.fabricGsm;
        band.fabricWeightUsedGrams = source.fabricWeightUsedGrams;
        band.materials = (source.materials || []).map(item => {
            let material = new OM.MaterialShareVM();
            material.materialIdentifier = item.materialIdentifier;
            material.materialName = item.materialName;
            material.percentage = item.percentage;
            return material;
        });
        return band;
    }

    private createEmptyBand(): OM.EditKnitBandVariantVM {
        let band = new OM.EditKnitBandVariantVM();
        band.quantity = 1;
        band.bandFlatWidthCm = 8;
        band.bandFlatLengthCm = 24;
        band.fabricGsm = 320;
        return band;
    }

    private resetTemporaryInputs() {
        this.tempMaterialIdentifier = "";
        this.tempPercentage = null;
        this.serverErrors = [];
    }
}
